import * as React from "react";
import clsx from "clsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createBase64Image } from "@/helpers/images";
import { getUserAvatarColor, getUserDisplayName, getUserInitials } from "@/helpers/userDisplay";

interface UserInfo {
  id?: string;
  avatar?: string;
  name?: string;
  surname?: string;
  email?: string;
}

interface Props {
  user?: UserInfo;
  identifier?: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses: Record<NonNullable<Props["size"]>, string> = {
  xs: "h-5 w-5 text-[9px]",
  sm: "h-6 w-6 text-[10px]",
  md: "h-7 w-7 text-xs",
  lg: "h-8 w-8 text-sm",
};

export const UserAvatar = React.forwardRef<HTMLSpanElement, Props>(
  ({ user, identifier, size = "md", className }, ref) => {
    const colorSeed = identifier || user?.id || user?.email || user?.name || "";
    const { bg, text } = getUserAvatarColor(colorSeed);
    const initials = getUserInitials(user);
    const displayName = getUserDisplayName(user);

    return (
      <Avatar ref={ref} className={clsx(sizeClasses[size], className)}>
        {user?.avatar && (
          <AvatarImage src={createBase64Image(user.avatar)} alt={displayName} className="rounded-full object-cover" />
        )}
        <AvatarFallback className={clsx("font-medium", bg, text)}>{initials}</AvatarFallback>
      </Avatar>
    );
  },
);

UserAvatar.displayName = "UserAvatar";
